import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Icon } from '@shared/icons';
import { excludeProps } from '@shared/theme/util';
import { Translate, type Translation } from '@shared/translation';
import type { ReactNode } from 'react';
import { tableTexts } from '../texts';

const Container = styled('div', { name: 'TableEmpty', slot: 'Container' })({
  display: 'flex',
  flexFlow: 'column nowrap',
  height: '100%',
  width: '100%',
  container: 'empty / inline-size',
});

const SpaceBefore = styled('div', { name: 'TableEmpty', slot: 'SpaceAbove' })({
  flex: 3,
});

const SpaceAfter = styled('div', { name: 'TableEmpty', slot: 'SpaceAfter' })({
  flex: 4,
});

const Content = styled('div', {
  name: 'TableEmpty',
  slot: 'Content',
  shouldForwardProp: (propName) => excludeProps(propName, ['margin']),
})<{ margin: EmptyMargin }>(({ theme, margin }) => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 0,
  gap: theme.spacing(1),
  margin: margin === 'small' ? theme.spacing(1) : theme.spacing(3, 4),
}));

const Text = styled(Typography, { name: 'TableEmpty', slot: 'Text' })(
  ({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    textAlign: 'center',
    '@container empty (min-width: 400px)': {
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
    },
  })
);

const Actions = styled('div', {
  name: 'TableEmpty',
  slot: 'Actions',
  shouldForwardProp: (propName) => excludeProps(propName, ['margin']),
})<{ margin: EmptyMargin }>(({ theme, margin }) => ({
  margin: theme.spacing(margin === 'small' ? 1 : 2),
}));

type EmptyMargin = 'small' | 'normal';

export interface EmptyStateProps {
  text?: Translation;
  actions?: ReactNode;
  margin?: EmptyMargin;
}

// TODO: This was copied from SAM for the table extraction. If another shared
// surface needs it, move it out to a generic shared UI EmptyState component.
export const EmptyState = ({
  text = tableTexts.empty.noContent,
  actions,
  margin = 'normal',
}: EmptyStateProps) => (
  <Container>
    <SpaceBefore />
    <Content margin={margin}>
      <Icon name="no_content" size="xxlarge" color="action.active" />
      <Text variant="body2">
        <Translate text={text} />
      </Text>
      {actions && <Actions margin={margin}>{actions}</Actions>}
    </Content>
    <SpaceAfter />
  </Container>
);
